/**
 * BranchList - the FULL branch list of S-DASH (UX-RW-03 v1.1 S-DASH "Branch
 * list"): never capped, the server's keyset order kept exactly (`dbName ASC`,
 * no client re-sort - D-9), "Branches (N)" with N the server's branches_total.
 *
 * A reachable row shows the branch name as a link to S-BRANCH and the three
 * counts with word labels; Failed and Pending are links to the branch lists
 * (D-15). An unreachable row shows the Unreachable chip and the server's
 * reason verbatim, carries NO counts and is not a link - there is no data
 * behind it, and it is never read as zero activity. Position honesty:
 * "Showing X of N branches" while more exist, "All N branches shown" at the
 * end; `hasMore === false` is the only stop signal.
 */

import type { PeriodSelection } from '@domain/types'
import type { BranchList as BranchListData, MoreState, TpinBranch } from '@domain/tpin'
import { branchesHeading, positionText } from '@domain/tpinDash'
import { branchPath, failedListPath, pendingListPath } from '@domain/tpinPaths'
import { CountLink, CountStatic } from './CountLink'
import { Link } from './Link'

/** The visible label of the per-row action (PD-35 delta 4); its accessible name adds the branch. */
export const OPEN_BRANCH_LABEL = 'Open branch'

function BranchRow({ tin, row, period }: { readonly tin: string; readonly row: TpinBranch; readonly period: PeriodSelection }) {
  if (row.kind === 'unreachable') {
    return (
      <li className="branch-row branch-row--unreachable" data-testid={`branch-row-${row.dbName}`}>
        <span className="branch-name">{row.dbName}</span>
        <span className="branch-chip-unreachable" data-testid={`branch-unreachable-${row.dbName}`}>
          Unreachable
        </span>
        {row.reason !== null && (
          <span className="branch-reason" data-testid={`branch-reason-${row.dbName}`}>
            {row.reason}
          </span>
        )}
      </li>
    )
  }
  return (
    <li className="branch-row" data-testid={`branch-row-${row.dbName}`}>
      <span className="branch-name">{row.dbName}</span>
      <div className="branch-counts">
        <CountStatic count={row.synced} statusKey="synced" statusLabel="Synced" scope={row.dbName} />
        <CountLink
          count={row.failed}
          dbName={row.dbName}
          noun="invoices"
          statusKey="failed"
          statusLabel="Failed"
          period={period}
          to={failedListPath(row.dbName)}
          testid={`branch-failed-${row.dbName}`}
        />
        <CountLink
          count={row.pending}
          dbName={row.dbName}
          noun="invoices"
          statusKey="pending"
          statusLabel="Pending"
          period={period}
          to={pendingListPath(row.dbName)}
          testid={`branch-pending-${row.dbName}`}
        />
      </div>
      {/* PD-35 delta 4: one visible action for the unchanged user-initiated drill. The branch name is
          plain text; this link is the only way into S-BRANCH from the row, so nothing triggers twice. */}
      <Link
        to={branchPath(tin, row.dbName)}
        period={period}
        className="branch-open"
        testid={`branch-link-${row.dbName}`}
        ariaLabel={`Open branch ${row.dbName}`}
      >
        {OPEN_BRANCH_LABEL}
      </Link>
    </li>
  )
}

export function BranchList({
  tin,
  list,
  total,
  more,
  period,
  onLoadMore,
}: {
  readonly tin: string
  readonly list: BranchListData
  /** The server's branches_total, or null while the summary is not complete. */
  readonly total: number | null
  readonly more: MoreState
  readonly period: PeriodSelection
  readonly onLoadMore: () => void
}) {
  return (
    <section className="branch-section" aria-labelledby="branches-heading" id="branches" data-testid="branch-section">
      <h2 id="branches-heading" className="branch-heading" data-testid="branches-heading">
        {branchesHeading(total)}
      </h2>
      {list.rows.length === 0 && !list.hasMore ? (
        <p role="status" className="state state-empty" data-testid="branches-empty">
          No branches registered for this TPIN
        </p>
      ) : (
        <>
          <ul className="branch-list" data-testid="branch-list">
            {list.rows.map((row) => (
              <BranchRow key={row.dbName} tin={tin} row={row} period={period} />
            ))}
          </ul>
          <p className="branch-position" aria-live="polite" data-testid="branch-position">
            {positionText(list.rows.length, total, list.hasMore)}
          </p>
          {more.kind === 'error' && (
            <p role="alert" className="state state-error" data-testid="branches-more-error">
              {more.message}
            </p>
          )}
          {list.hasMore && (
            <button type="button" className="next-page-button" onClick={onLoadMore} disabled={more.kind === 'loading'} data-testid="branches-load-more">
              {more.kind === 'loading' ? 'Loading…' : more.kind === 'error' ? 'Retry loading more' : 'Load more'}
            </button>
          )}
        </>
      )}
    </section>
  )
}
